import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Popup } from '@components/atoms/Popup';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Table } from '@components/atoms/Table';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useGetMyAccountStoreDetail } from '@services/useGetMyAccountStoreDetail';
import { useDeleteStoreImage } from '@services/useDeleteStoreImage';
import { useAddStoreImages } from '@services/useAddStoreImages';
import { InputErrors } from '@types';
import { convertFilesToBase64 } from '@utils/image';
import styles from '../../MyCompany.module.css';

interface EditGalleryFormErrors {
    images: InputErrors;
}

interface EditGalleryModalProps {
    show: boolean;
    onClose: () => void;
}

export const EditGalleryModal: FC<EditGalleryModalProps> = ({ show, onClose }) => {
    const { showDefaultNotification } = useNotifications();
    const { myAccountStoreDetail } = useData();
    const {
        request: requestUpload,
        loading,
        success: successUpload,
        errorMessage,
        validationErrors,
        lastResponseTime: lastUpload,
    } = useAddStoreImages();
    const { request: requestMyAccountStoreDetail } = useGetMyAccountStoreDetail();
    const { t } = useTranslation();

    const translations = {
        title: t('edit-store-gallery-modal-component.title'),
        imagesLabel: t('edit-store-gallery-modal-component.images.label'),
        imagesRequiredError: t('edit-store-gallery-modal-component.images.required-error'),
        submitButton: t('edit-store-gallery-modal-component.save.button'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [images, setImages] = useState<File[]>([]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<EditGalleryFormErrors>({
        images: [],
    });

    // force the input to reset
    const [imagesInputKey, setImagesInputKey] = useState(Date.now());

    const handleReset = () => {
        setImagesInputKey(Date.now());
    };

    const handleOnClickSubmitImages = () => {
        const errors = validateForm();

        if (errors.images.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditGalleryFormErrors = () => {
        const newErrors: EditGalleryFormErrors = {
            images: [],
        };

        images.length === 0 && newErrors.images.push(translations.imagesRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        const imageInput = document.getElementById('images-input') as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64Images: any = imageInput.files ? await convertFilesToBase64(imageInput.files) : null;

        requestUpload({ uuid: myAccountStoreDetail?.data?.uuid || '' }, { images: base64Images || [] });
    };

    // dispatch notification, according to feedback of adding images
    useEffect(() => {
        if (lastUpload) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage });
            } else if (validationErrors) {
                const firstImageErrorKey = Object.keys(validationErrors).find((key) => key.startsWith('image'));
                setErrors({
                    // match form request errors with frontend errors object
                    images: firstImageErrorKey && (validationErrors?.[firstImageErrorKey] || []),
                });
            } else {
                showDefaultNotification({ type: 'success', message: successUpload || '' });
                // update images input
                handleReset();
                setImages([]);
                requestMyAccountStoreDetail({ uuid: myAccountStoreDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUpload]);

    return (
        <Popup
            show={show}
            onHide={() => onClose()}
            desktopWidth="1220px"
            fullWidthOnMobile={true}
            header={
                <>
                    <div className={styles.header}>
                        <Typography tagVariant={TypographyTagVariants.h5} fontWeight="900" fontSize="20">
                            {translations.title}
                        </Typography>
                    </div>
                    <hr className={styles.divider} />
                </>
            }>
            <div className={styles['content-container']}>
                {/* new images */}
                <Form formRef={formRef} onSubmit={() => submitForm()}>
                    <div className="row">
                        <div className="col-md-6 col-12">
                            <Input
                                key={imagesInputKey}
                                id="images-input"
                                type={InputTypes.file}
                                label={`${translations.imagesLabel}*`}
                                variant={InputVariants.labeledInput}
                                multiple={true}
                                value=""
                                onChange={(value: FileList | null) => {
                                    const imagesArray: File[] = Array.from(value || []);
                                    setImages(imagesArray || []);
                                }}
                                state={errors.images.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.images}
                            />
                        </div>

                        {/* submit */}
                        <div className="col-md-6 col-12 pt-1 text-end">
                            <Button
                                icon="fa-solid fa-floppy-disk"
                                label={translations.submitButton}
                                mode={ButtonMode.primary}
                                onClick={() => handleOnClickSubmitImages()}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </Form>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                {/* current images */}
                <ImagesTable />
            </div>
        </Popup>
    );
};

const ImagesTable: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const { myAccountStoreDetail } = useData();
    const {
        request: requestDelete,
        loading,
        error: errorDelete,
        success: successDelete,
        lastResponseTime,
    } = useDeleteStoreImage();
    const { request: requestMyAccountStoreDetail } = useGetMyAccountStoreDetail();
    const { t } = useTranslation();

    const translations = {
        image: t('edit-store-gallery-modal-component.images.header.image'),
        actions: t('edit-store-gallery-modal-component.images.header.actions'),
        deletePopupTitle: t('edit-store-gallery-modal-component.images.delete-popup.title'),
        deletePopupDescription: t('edit-store-gallery-modal-component.images.delete-popup.description'),
        deletePopupWarning: t('edit-store-gallery-modal-component.images.delete-popup.warning'),
        galleryAlt: (index: number) =>
            t('edit-store-gallery-modal-component.images.gallery.alt', {
                index: `${index}`,
                name: `${myAccountStoreDetail?.data?.commercial_name}`,
            }),
    };

    // uuid of image being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // dispatch notification, according to feedback of removal of image
    useEffect(() => {
        if (lastResponseTime) {
            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                if (successDelete) {
                    showDefaultNotification({ type: 'success', message: successDelete });
                }
                // get updated images
                const storeUuid = myAccountStoreDetail?.data?.uuid;
                requestMyAccountStoreDetail({ uuid: storeUuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <>
            {myAccountStoreDetail?.data?.images && myAccountStoreDetail.data.images.length > 0 && (
                <>
                    <Table
                        borderless
                        headerCols={[
                            { classes: 'text-start', text: translations.image },
                            { classes: 'text-end', text: translations.actions },
                        ]}
                        contentRows={myAccountStoreDetail.data.images.map((image, index) => {
                            return [
                                {
                                    classes: 'text-start',
                                    content: (
                                        <img
                                            className={styles['store-image']}
                                            src={image.src}
                                            alt={translations.galleryAlt(index + 1)}
                                        />
                                    ),
                                },
                                {
                                    classes: 'text-end',
                                    content: (
                                        <Table.Options
                                            options={[
                                                <button
                                                    key={image.uuid}
                                                    type="button"
                                                    disabled={loading}
                                                    onClick={() => setShowDeleteModal(image.uuid || null)}>
                                                    <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                        <i className="fa-light fa-trash-can" />
                                                    </Typography>
                                                </button>,
                                            ]}
                                        />
                                    ),
                                },
                            ];
                        })}
                    />

                    {/* popup to confirm deletion of image */}
                    <PopupDelete
                        question={translations.deletePopupTitle}
                        confirmation={translations.deletePopupDescription}
                        warning={translations.deletePopupWarning}
                        onClickConfirm={handleOnConfirmDelete}
                        show={showDeleteModal !== null}
                        onHide={() => setShowDeleteModal(null)}
                    />
                </>
            )}
        </>
    );
};
